import { z } from 'zod';
// Ref: `prompt` in https://openid.net/specs/openid-connect-core-1_0.html#AuthRequest
export var OidcPrompt;
(function (OidcPrompt) {
    OidcPrompt["None"] = "none";
    OidcPrompt["Login"] = "login";
    OidcPrompt["Consent"] = "consent";
    OidcPrompt["SelectAccount"] = "select_account";
})(OidcPrompt || (OidcPrompt = {}));
export const oidcPromptsGuard = z
    .nativeEnum(OidcPrompt)
    .array()
    .optional();
// Copied from https://github.com/colinhacks/zod#json-type
const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const jsonGuard = z.lazy(() => z.union([literalSchema, z.array(jsonGuard), z.record(jsonGuard)]));
export const jsonObjectGuard = z.record(jsonGuard);
export const socialUserInfoGuard = z.object({
    id: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    name: z.string().optional(),
    avatar: z.string().optional(),
    rawData: jsonGuard.optional(),
});
export const connectorSessionGuard = z
    .object({
    nonce: z.string(),
    redirectUri: z.string(),
    connectorId: z.string(),
    connectorFactoryId: z.string(),
    jti: z.string(),
    state: z.string(),
})
    .partial()
    // Accept arbitrary unspecified keys so developers who can not publish @rivenai/connector-kit can more flexibly utilize connector session.
    .catchall(z.unknown());
export const tokenResponseGuard = z.object({
    id_token: z.string().optional(),
    access_token: z.string().optional(),
    refresh_token: z.string().optional(),
    expires_in: z.number().or(z.string()).optional(),
    scope: z.string().optional(),
    token_type: z.string().optional(),
});
export const googleOneTapConfigGuard = z.object({
    isEnabled: z.boolean().optional(),
    autoSelect: z.boolean().optional(),
    closeOnTapOutside: z.boolean().optional(),
    itpSupport: z.boolean().optional(),
});
/**
 * An object that contains the configuration for the official Google connector.
 *
 * @remarks
 * Unlike other connectors, the Google connector supports Google One Tap which needs additional
 * configuration and special handling in our system. So we put the constants and configuration
 * in this package for reusability, rather than hardcoding them in our system.
 *
 * Other connectors should not follow this pattern unless there is a strong reason to do so.
 */
export const GoogleConnector = Object.freeze({
    /** The target of Google connectors. */
    target: 'google',
    /** The factory ID of the official Google connector. */
    factoryId: 'google-universal',
    // TODO: update google connector as well, keep it unchanged for now since it's out of scope.
    /**
     * The URI of the Google JWKS. Used to verify the Google-issued JWT.
     * @see {@link https://developers.google.com/identity/gsi/web/guides/verify-google-id-token | Verify the Google ID token on your server side}
     */
    jwksUri: 'https://www.googleapis.com/oauth2/v3/certs',
    /** The issuers of the Google JWKS. Used to verify the Google-issued JWT. */
    issuer: ['https://accounts.google.com', 'accounts.google.com'],
    oneTapParams: Object.freeze({
        /** The parameter Google One Tap uses to prevent CSRF attacks. */
        csrfToken: 'g_csrf_token',
        /** The parameter Google One Tap uses to carry the ID token. */
        credential: 'credential',
    }),
    configGuard: z.object({
        clientId: z.string(),
        clientSecret: z.string(),
        scope: z.string().optional(),
        prompts: oidcPromptsGuard,
        oneTap: googleOneTapConfigGuard.optional(),
        offlineAccess: z.boolean().optional(),
    }),
});
/**
 * Checks if the given social provider data is from Google One Tap.
 *
 * Google One Tap data can come from:
 * 1. Riven Auth's built-in Google One Tap button (sign-in experience).
 * 2. An external Google One Tap button (added by the application itself).
 *
 * To check specifically for external One Tap, use `isExternalGoogleOneTap`.
 * @param data - The data from the social provider.
 * @returns Whether the data is from Google One Tap.
 */
export const isGoogleOneTap = (data) => {
    return Boolean(data[GoogleConnector.oneTapParams.credential]);
};
/**
 * Checks if the given social provider data is from an external Google One Tap button
 * (not Riven Auth's sign-in experience).
 *
 * External Google One Tap data does not include a CSRF token, so different handling
 * and security measures are required.
 *
 * @param data - The data from the social provider.
 * @returns Whether the data is from external Google One Tap.
 */
export const isExternalGoogleOneTap = (data) => {
    return isGoogleOneTap(data) && !data[GoogleConnector.oneTapParams.csrfToken];
};
export const rivenAuthGoogleOneTapCookieKey = '_riven_google_one_tap_credential';
